import {ProductionSiteDto} from '../../../../models/generated-models';

const trim = (value: string | null | undefined): string => `${value ?? ''}`.trim();

/** "ซอย 2" / "ถนนช้างม่อย": add the word unless the value already starts with it (or its abbreviation). */
const withWord = (value: string, word: string, abbreviations: string[]): string =>
  [word, ...abbreviations].some((prefix) => value.startsWith(prefix)) ? value : `${word}${value}`;

/**
 * The production site as one address line, the way a Thai address is read:
 * `แปลงลำไย · 683 หมู่ 5 ซอย 2 ถนนช้างม่อย แขวงเสนานิคม เขตจตุจักร กรุงเทพมหานคร 10900`.
 * Bangkok reads แขวง/เขต, everywhere else ต./อ./จ.; thaitraces already writes "เขต…" into the
 * district of a Bangkok site, so a prefix the name carries is never doubled. Empty when the site
 * has no address part at all.
 */
export function formatProductionSite(site: ProductionSiteDto | null | undefined): string {
  if (!site) {
    return '';
  }
  const province = trim(site.provinceName);
  const bangkok = province.startsWith('กรุงเทพ');
  const parts: string[] = [];
  const addressNo = trim(site.addressNo);
  if (addressNo) {
    parts.push(addressNo);
  }
  const moo = trim(site.moo);
  if (moo) {
    parts.push(withWord(moo, 'หมู่ ', ['หมู่', 'ม.']));
  }
  const soi = trim(site.soi);
  if (soi) {
    parts.push(withWord(soi, 'ซอย ', ['ซอย', 'ซ.']));
  }
  const road = trim(site.road);
  if (road) {
    parts.push(withWord(road, 'ถนน', ['ถนน', 'ถ.']));
  }
  const subdistrict = trim(site.subdistrictName);
  if (subdistrict) {
    parts.push(withWord(subdistrict, bangkok ? 'แขวง' : 'ต.', ['แขวง', 'ตำบล', 'ต.']));
  }
  const district = trim(site.districtName);
  if (district) {
    parts.push(withWord(district, bangkok ? 'เขต' : 'อ.', ['เขต', 'อำเภอ', 'อ.']));
  }
  if (province) {
    parts.push(bangkok ? province : withWord(province, 'จ.', ['จังหวัด', 'จ.']));
  }
  const postcode = trim(site.postcode);
  if (postcode) {
    parts.push(postcode);
  }
  const address = parts.join(' ');
  const name = trim(site.name);
  return [name, address].filter((part) => !!part).join(' · ');
}

/** The shortest thing that tells two sites apart on a chip: the sub-district, else the district, else the province. */
export function formatProductionSiteShort(site: ProductionSiteDto | null | undefined): string {
  if (!site) {
    return '';
  }
  return trim(site.subdistrictName) || trim(site.districtName) || trim(site.provinceName) || trim(site.name);
}
